import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, CornerDownRight, Link2, Pencil } from 'lucide-react';
import { useTickets } from '../contexts/TicketsProvider';
import { fetchTicket } from '../lib/api';
import { TYPE_MAP, STATUS_MAP, PRIORITY_MAP, DEPARTMENT_MAP, RECURRENCE_MAP, isRecurring } from '../lib/constants';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { formatDueDate } from '../lib/dates';
import { cn } from '../lib/utils';
import Spinner from '../components/Spinner';
import MessageThread from '../components/MessageThread';
import UserAvatar from '../components/UserAvatar';
import SubtaskList from '../components/SubtaskList';

const fmt = (s) => (s ? new Date(s).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '');

function Meta({ label, value }) {
  return (
    <div className="flex gap-2">
      <span className="w-20 shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 truncate text-foreground">{value}</span>
    </div>
  );
}

export default function TicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { openEditTicket, tickets } = useTickets();

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true); setNotFound(false);
    fetchTicket(id)
      .then((t) => { if (alive) setTicket(t); })
      .catch(() => { if (alive) setNotFound(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id]);

  // Keep in sync when the ticket is edited in the modal.
  useEffect(() => {
    const fromCtx = tickets.find((t) => String(t.id) === String(id));
    if (fromCtx) setTicket((prev) => (prev ? { ...prev, ...fromCtx } : prev));
  }, [tickets, id]);

  const share = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/tickets/${id}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable */ }
  };

  if (loading) return <div className="p-6"><Spinner /></div>;
  if (notFound || !ticket) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        Ticket not found. <button className="text-primary hover:underline" onClick={() => navigate('/board')}>Back to board</button>
      </div>
    );
  }

  const type = TYPE_MAP[ticket.type];
  const status = STATUS_MAP[ticket.status];
  const priority = PRIORITY_MAP[ticket.priority];
  const dept = DEPARTMENT_MAP[ticket.department];
  // Neighbours in a recurring series, resolved from the loaded ticket list.
  const nextOccurrence = tickets.find((t) => t.recurred_from_id === ticket.id);
  const occurrenceLink = (label, tid) => (
    <button type="button" onClick={() => navigate(`/tickets/${tid}`)} className="text-primary hover:underline">
      {label} #{tid}
    </button>
  );

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-4 flex items-center justify-between">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-muted-foreground transition hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> back
        </button>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={share} className="gap-1.5">
            {copied ? <><Check className="h-3.5 w-3.5" /> copied</> : <><Link2 className="h-3.5 w-3.5" /> share</>}
          </Button>
          <Button variant="outline" size="sm" onClick={() => openEditTicket(ticket)} className="gap-1.5">
            <Pencil className="h-3.5 w-3.5" /> edit
          </Button>
        </div>
      </div>

      {/* Wide screens: ticket on the left, message thread on the right. */}
      <div className="grid gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] xl:items-start">
      <div className="rounded-xl border border-border bg-card p-5">
        {ticket.parent ? (
          <button type="button" onClick={() => navigate(`/tickets/${ticket.parent.id}`)} className="mb-2 flex items-center gap-1 text-xs text-muted-foreground hover:text-primary">
            <CornerDownRight className="h-3 w-3" /> subtask of #{ticket.parent.id} {ticket.parent.title}
          </button>
        ) : null}
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="text-muted-foreground">#{ticket.id}</span>
          <Badge className={type?.badge}>{type?.key}</Badge>
          {dept ? <Badge className={dept.badge}>{dept.label}</Badge> : null}
          <span className="inline-flex items-center gap-1"><span className={cn('h-2 w-2 rounded-full', status?.dot)} />{status?.key}</span>
          <span className="inline-flex items-center gap-1"><span className={cn('h-2 w-2 rounded-full', priority?.dot)} />{priority?.key}</span>
        </div>
        <h1 className={cn('mt-2 text-xl font-semibold leading-snug', ticket.status === 'done' && 'text-muted-foreground line-through')}>
          {ticket.title}
        </h1>

        <div className="mt-3 grid gap-x-6 gap-y-1.5 text-sm sm:grid-cols-2">
          <Meta label="requestor" value={ticket.requestor?.name || '—'} />
          <Meta
            label="assignees"
            value={ticket.assignees?.length ? (
              <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
                {ticket.assignees.map((a) => (
                  <span key={a.id} className="inline-flex items-center gap-1">
                    <UserAvatar user={a} className="h-4 w-4 rounded-sm" /> {a.name}
                  </span>
                ))}
              </span>
            ) : 'unassigned'}
          />
          <Meta label="due" value={ticket.due_date ? formatDueDate(ticket.due_date) : '—'} />
          <Meta label="estimate" value={ticket.estimate_hours != null ? ticket.estimate_hours + 'h' : '—'} />
          {isRecurring(ticket) ? (
            <Meta
              label="repeats"
              value={(
                <span className="inline-flex flex-wrap items-center gap-x-2">
                  <span>{RECURRENCE_MAP[ticket.recurrence]?.label.toLowerCase()}</span>
                  {ticket.recurred_from_id ? occurrenceLink('previous', ticket.recurred_from_id) : null}
                  {nextOccurrence ? occurrenceLink('next', nextOccurrence.id) : null}
                </span>
              )}
            />
          ) : null}
          <Meta label="created" value={fmt(ticket.created_at)} />
          {ticket.closed_at ? <Meta label="closed" value={fmt(ticket.closed_at)} /> : null}
        </div>

        {ticket.description ? <p className="mt-4 whitespace-pre-wrap text-sm leading-relaxed">{ticket.description}</p> : null}

        {!ticket.parent ? (
          <div className="mt-5 border-t border-border pt-4">
            <SubtaskList parent={ticket} />
          </div>
        ) : null}

        {ticket.attachments?.length ? (
          <div className="mt-4 flex flex-wrap gap-2">
            {ticket.attachments.map((a) => (
              <a key={a.id} href={a.url} target="_blank" rel="noreferrer" className="block h-16 w-16 overflow-hidden rounded-md border border-border">
                <img src={a.url} alt={a.original_name} className="h-full w-full object-cover" />
              </a>
            ))}
          </div>
        ) : null}
      </div>

      <div>
        <MessageThread ticketId={id} />
      </div>
      </div>
    </div>
  );
}
